import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { KeyRound, Trash2, ShieldCheck, Loader2, PenTool, Eraser, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Compact signature pad — reused for on-file capture
function InlineSignaturePad({ onDataUrl, height = 140 }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: (t ? t.clientX : e.clientX) - rect.left, y: (t ? t.clientY : e.clientY) - rect.top };
  };
  const start = (e) => { drawing.current = true; last.current = getPos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = "#0f172a"; ctx.lineWidth = 2; ctx.lineCap = "round";
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p;
  };
  const end = () => { if (drawing.current) { drawing.current = false; onDataUrl(canvasRef.current.toDataURL("image/png")); } };
  const clear = () => { canvasRef.current.getContext("2d").clearRect(0, 0, canvasRef.current.width, canvasRef.current.height); onDataUrl(""); };
  return (
    <div className="border border-slate-300 rounded-lg bg-white">
      <canvas ref={canvasRef} width={480} height={height} className="w-full touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <div className="flex items-center justify-between px-2 py-1 border-t bg-slate-50">
        <span className="text-xs text-slate-500 flex items-center gap-1"><PenTool className="w-3 h-3" /> Draw signature</span>
        <Button variant="ghost" size="sm" onClick={clear} className="h-6 text-xs gap-1"><Eraser className="w-3 h-3" />Clear</Button>
      </div>
    </div>
  );
}

export default function ClientSignaturePinCard({ clientId, isAuthorized }) {
  const [status, setStatus] = useState({ has_pin: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [signatureOnFile, setSignatureOnFile] = useState("");

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/evv/clients/${clientId}/signature-pin-status`);
      setStatus(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const savePin = async () => {
    if (!/^\d{4}$/.test(pin)) {
      toast.error("PIN must be exactly 4 digits");
      return;
    }
    if (pin !== confirmPin) {
      toast.error("PIN values do not match");
      return;
    }
    if (!signatureOnFile) {
      toast.error("Please draw the client's signature to keep on file");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/evv/clients/${clientId}/signature-pin`, {
        pin,
        signature_on_file: signatureOnFile,
      });
      toast.success("PIN + signature-on-file saved. Valid for 30 days.");
      setPin("");
      setConfirmPin("");
      setSignatureOnFile("");
      setOpen(false);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save PIN");
    } finally {
      setSaving(false);
    }
  };

  const clearPin = async () => {
    if (!window.confirm("Remove the PIN? The client will need to sign manually on the next visit.")) return;
    try {
      await axios.delete(`${API}/evv/clients/${clientId}/signature-pin`);
      toast.success("Signature PIN removed");
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove PIN");
    }
  };

  if (loading) return null;

  const hasPin = !!status.has_pin;
  const isExpired = !!status.signature_on_file_expired;
  const expiresAt = status.signature_on_file_expires_at;
  const setAt = status.set_at;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="client-pin-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${hasPin && !isExpired ? "bg-emerald-100" : isExpired ? "bg-amber-100" : "bg-slate-100"}`}>
            <KeyRound className={`w-5 h-5 ${hasPin && !isExpired ? "text-emerald-600" : isExpired ? "text-amber-600" : "text-slate-500"}`} />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-medium text-slate-900">Client Signature PIN</p>
              {hasPin && !isExpired && (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1">
                  <ShieldCheck className="w-3 h-3" />
                  Configured
                </Badge>
              )}
              {isExpired && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Signature expired
                </Badge>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1 max-w-md">
              {isExpired
                ? "The on-file signature is more than 30 days old. Next PIN entry will still verify but the caregiver will be prompted to have the client re-draw their signature."
                : hasPin
                ? `Set ${setAt ? new Date(setAt).toLocaleDateString() : ""}. Signature-on-file expires ${expiresAt ? new Date(expiresAt).toLocaleDateString() : ""}. Caregivers can enter the PIN to auto-embed the signature on visit forms.`
                : "Set a 4-digit PIN + save the client's signature so caregivers can PIN-verify future visits (signature auto-embeds on forms). Signature is refreshed every 30 days per policy."}
            </p>
          </div>
        </div>
        {isAuthorized && (
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPin("");
                setConfirmPin("");
                setSignatureOnFile("");
                setOpen(true);
              }}
              data-testid="set-client-pin-btn"
            >
              {hasPin ? "Update" : "Set PIN"}
            </Button>
            {hasPin && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearPin}
                className="text-red-600 hover:bg-red-50"
                data-testid="clear-client-pin-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{hasPin ? "Update" : "Set"} Signature PIN</DialogTitle>
            <DialogDescription>
              Pick a 4-digit PIN and capture the client's signature. Future PIN entries auto-embed this signature on the visit form. Signature must be refreshed every 30 days.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">4-digit PIN</label>
                <Input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder="0000"
                  className="text-center text-xl tracking-widest font-mono"
                  data-testid="pin-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Confirm</label>
                <Input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder="0000"
                  className="text-center text-xl tracking-widest font-mono"
                  data-testid="pin-confirm-input"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">
                Client's signature <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-slate-500 mb-1">
                Hand the phone to the client (or draw for them if authorized) — this signature is embedded on every PIN-verified visit form for the next 30 days.
              </p>
              <InlineSignaturePad onDataUrl={setSignatureOnFile} height={140} />
              {signatureOnFile && (
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" /> Signature captured
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              onClick={savePin}
              disabled={saving || !signatureOnFile || pin.length !== 4 || confirmPin.length !== 4}
              data-testid="save-pin-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save PIN + Signature"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
